import { Link } from "@mui/material";
import CopyToBufferButton from "@entrilia/shared/components/inputs/CopyToBufferButton";
import { logError } from "@entrilia/shared/logging";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import type { ValidationResult, Validator } from "@entrilia/shared/utilities/validators";
import { combineValidators, emailValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import type { EntityFieldComponentProps, EntityFieldComponentPropsBase } from "../entityFieldTypes";
import { useFieldValuesContext } from "../FieldValuesContext";
import { getInvalidFieldValueError } from "../helpers";
import EntityTextFieldComponent from "./EntityTextFieldComponent";

const EntityEmailField = ({
  value,
  fieldId,
  fieldConfiguration,
  fieldAttributes,
  onChange,
  fieldInputId,
}: EntityFieldComponentPropsBase) => {
  if (typeof value !== "string" && value !== undefined && value !== null) {
    logError(getInvalidFieldValueError(fieldId, fieldConfiguration?.$type, value), "[EntityEmailField]");
    return null;
  }

  return (
    <EntityEmailFieldComponent
      value={value}
      fieldId={fieldId}
      fieldConfiguration={fieldConfiguration}
      fieldAttributes={fieldAttributes}
      onChange={onChange}
      fieldInputId={fieldInputId}
    />
  );
};

const EntityEmailFieldComponent = ({
  value,
  fieldId,
  fieldConfiguration,
  fieldAttributes,
  onChange,
  fieldInputId,
}: EntityFieldComponentProps<string>) => {
  const { emailFieldEditMode } = useFieldValuesContext();

  const editMode = emailFieldEditMode?.fieldId === fieldId ? emailFieldEditMode : undefined;

  const handleFieldValueChange = (value: string | undefined, fieldId: string, validationResult: ValidationResult) => {
    const sanitizedValue = value === undefined ? undefined : sanitizeEmail(value);
    //save empty email if not required as not defined value, the same behavior as for other fields. Backend validation does not allow empty email
    onChange(
      sanitizedValue === undefined || sanitizedValue === "" ? undefined : sanitizedValue,
      fieldId,
      validationResult
    );
  };

  const baseValidator = fieldAttributes.includes("Required")
    ? combineValidators(requiredValidator, emailValidator)
    : emailValidator;
  const validator: Validator<string> = (value) => baseValidator(sanitizeEmail(value));

  return (
    <>
      <EntityTextFieldComponent
        value={value || ""}
        fieldId={fieldId}
        fieldConfiguration={fieldConfiguration}
        fieldAttributes={fieldAttributes}
        onChange={handleFieldValueChange}
        fieldInputId={fieldInputId}
        validator={validator}
        displayValueComponent={
          value ? (
            <Link
              sx={(t) => ({ color: t.palette.text.primary })}
              onClick={(e) => e.stopPropagation()}
              href={`mailto:${value}`}
              underline="hover"
              target="_blank"
            >
              {value}
            </Link>
          ) : null
        }
        displayHoverComponent={value ? <CopyToBufferButton textToCopy={value} /> : null}
        editViaDialog={editMode?.kind === "dialog"}
        onEditDialogOpen={editMode?.kind === "dialog" ? editMode.onEdit : undefined}
        readonlyReason={editMode?.kind === "disabled" ? editMode.reason : undefined}
      />
    </>
  );
};

export default EntityEmailField;
